import {
  ApplySchemaAttributes,
  command,
  CommandFunction,
  extension,
  ExtensionTag,
  getTextSelection,
  keyBinding,
  KeyBindingProps,
  MarkExtension,
  MarkExtensionSpec,
  MarkSpecOverride,
  NamedShortcut,
  PrimitiveSelection,
  ProsemirrorAttributes,
  Static,
  toggleMark,
} from '@remirror/core';
import { CoreIcon } from '@remirror/icons';
import React from 'react';
import { InputRule, markInputRule } from 'remirror';

import { WFormatTypeProperty } from './_interface';

const CREATE_COMMAND_LABEL = 'Toggle Underline';

const toggleWFormatUnderlineOptions: Remirror.CommandDecoratorOptions = {
  icon: 'underline',
  label: ({ t }) => t(CREATE_COMMAND_LABEL),
  shortcut: NamedShortcut.Underline,
};

export type WFormatUnderlineExtensionAttributes = ProsemirrorAttributes<{
  type?: WFormatTypeProperty;
}>;

export interface WFormatUnderlineOptions {
  type?: Static<WFormatTypeProperty>;
  defaultType?: Static<WFormatTypeProperty>;
}

@extension<WFormatUnderlineOptions>({
  defaultOptions: {
    type: undefined,
    defaultType: undefined,
  },
  staticKeys: ['defaultType', 'type'],
})
export class WFormatUnderlineExtension extends MarkExtension<WFormatUnderlineOptions> {
  get name() {
    return 'wFormatUnderline' as const;
  }

  createTags() {
    return [ExtensionTag.FormattingMark, ExtensionTag.FontStyle];
  }

  createMarkSpec(extra: ApplySchemaAttributes, override: MarkSpecOverride): MarkExtensionSpec {
    return {
      ...override,
      attrs: {
        ...extra.defaults(),
        type: { default: this.options.defaultType },
      },
      parseDOM: [
        {
          tag: `w-format[type=underline]`,
          getAttrs: (element: string | Node) => ({
            ...extra.parse(element),
            type: 'underline',
          }),
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: (mark) => {
        const { type } = this.options;
        return ['w-format', { type: mark.attrs.type.toString() }, 0];
      },
    };
  }

  createInputRules(): InputRule[] {
    return [
      markInputRule({
        regexp: /(?:\*\*|__)([^*_]+)(?:\*\*|__)$/,
        type: this.type,
        ignoreWhitespace: true,
      }),
    ];
  }

  @command(toggleWFormatUnderlineOptions)
  toggleWFormatUnderline(attrs?: WFormatUnderlineExtensionAttributes, selection?: PrimitiveSelection): CommandFunction {
    return toggleMark({ type: this.type, selection, attrs: { ...attrs, type: 'underline' } });
  }

  @command()
  setWFormatUnderline(selection?: PrimitiveSelection): CommandFunction {
    return ({ tr, dispatch }) => {
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);
      dispatch?.(tr.addMark(from, to, this.type.create()));

      return true;
    };
  }

  @command()
  removeWFormatUnderline(selection?: PrimitiveSelection): CommandFunction {
    return ({ tr, dispatch }) => {
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);

      if (!tr.doc.rangeHasMark(from, to, this.type)) {
        return false;
      }

      dispatch?.(tr.removeMark(from, to, this.type));

      return true;
    };
  }

  @keyBinding({ shortcut: NamedShortcut.Underline, command: 'toggleWFormatUnderline' })
  shortcut(props: KeyBindingProps): boolean {
    return this.toggleWFormatUnderline()(props);
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Remirror {
    interface AllExtensions {
      wFormatUnderline: WFormatUnderlineExtension;
    }
  }
}
